import { property } from 'underscore';
import React, { useCallback, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import {
  Wizard,
  Layout,
  LayoutContainer,
  StepContainer,
} from '@bingads-webui-react/wizard';
import { StepList, BusyBlocker, CancelWizardDialog, DefaultTemplate } from '@bingads-webui-react/wizard-ui-library';
import { withSiteMap, getRoutePathById } from '@bingads-webui-campaign-react/with-site-map';
import { WizardPerfMarker } from '@bingads-webui-campaign/wizard-performance-marker';
import { WizardStepLogger } from '@bingads-webui-campaign-react/step-wizard-logger';
import { Activity, Scenario, ActivityContext } from '@bingads-webui-react/hoc-with-signal';
import { ContentFooter } from '@bingads-webui-campaign-react/content-footer';

import { CreationFooter } from '../shared/creation-footer';
import { SingleStepLoggerUnderActivity } from '../shared/logger-under-activity';
import { withStepActivity } from '../shared/with-step-activity';

const StepContainerWithStepActivity = withStepActivity(StepContainer);
const StepFooterWithStepActivity = withStepActivity(CreationFooter);
const CancelWizardDialogWithStepActivity = withStepActivity(CancelWizardDialog);

const CreationWizardInternal = (props) => {
  const {
    getStepListConfig,
    permissions,
    wizardLoggerFlowName,
    wizardLoggerSubFlowName,
    wizardLoggerStepToPage,
  } = props;
  const i18n = useContext(I18nContext);

  const stepListConfig = useMemo(() => getStepListConfig({
    i18n,
    showExtensionsStep: property(['dynamic', 'SearchRevampPhase1Post'])(permissions),
  }), [getStepListConfig, i18n, permissions]);

  const activity = useContext(ActivityContext);
  const writeWizardLog = useCallback((...args) => {
    activity.signal(...args);
  }, [activity]);

  return (
    <WizardStepLogger
      writeLog={writeWizardLog}
      flow={wizardLoggerFlowName}
      subflow={wizardLoggerSubFlowName}
      stepToPage={wizardLoggerStepToPage}
      shouldPost
    >
      <LayoutContainer template={DefaultTemplate}>
        <Layout type="Blocker" component={BusyBlocker} />
        <Layout type="StepList" component={StepList} config={stepListConfig} />
        <Layout type="StepContainer" component={StepContainerWithStepActivity} />
        <Layout type="Footer" component={StepFooterWithStepActivity} nextButtonText={i18n.getString(_TL_('Save & go to next step'))} />
        <Layout type="CancelDialog" component={CancelWizardDialogWithStepActivity} />
      </LayoutContainer>
    </WizardStepLogger>
  );
};

CreationWizardInternal.propTypes = {
  getStepListConfig: PropTypes.func.isRequired,
  permissions: PropTypes.shape({}).isRequired,
  wizardLoggerFlowName: PropTypes.string.isRequired,
  wizardLoggerSubFlowName: PropTypes.string.isRequired,
  wizardLoggerStepToPage: PropTypes.shape({}).isRequired,
};

export const CreationWizardWithActivity = (props) => {
  const {
    scenarioName,
    activityName,
    stepConfigs,
    perfMarker,
    scope,
    siteMap,
    perfMarkerName,
    onDoneNavigateToSiteMapId,
    initialFlowFactors,
    getFlow,
    isLegalFooter,
    dynamicOptions,
  } = props;

  const wizardPerfMarker = useMemo(() => (
    perfMarker.createChild(perfMarkerName)
  ), [perfMarker, perfMarkerName]);

  const routeToNavigateToWhenDone = useMemo(() => getRoutePathById(siteMap, onDoneNavigateToSiteMapId), [onDoneNavigateToSiteMapId, siteMap]);

  const config = useMemo(() => {
    const newStepConfigs = stepConfigs.map(stepConfig => ({
      ...stepConfig,
      view: viewProps => (
        <>
          {stepConfig.view({ ...viewProps, wizardPerfMarker })}
          <SingleStepLoggerUnderActivity />
        </>
      ),
    }));

    return {
      id: 'search-creation-wizard',
      steps: newStepConfigs,
      actions: {
        onDone: () => scope.save(routeToNavigateToWhenDone),
        onExited: () => scope.save(routeToNavigateToWhenDone),
      },
      initialFlowFactors,
      initialWizardData: dynamicOptions,
      getFlow,
    };
  }, [dynamicOptions, getFlow, initialFlowFactors, routeToNavigateToWhenDone, scope, stepConfigs, wizardPerfMarker]);

  return (
    <Scenario name={scenarioName}>
      <Activity name={activityName}>
        <Wizard config={config}>
          <WizardPerfMarker wizardName={perfMarkerName} wizardMarker={wizardPerfMarker}>
            <CreationWizardInternal {...props} />
          </WizardPerfMarker>
          {isLegalFooter && <ContentFooter />}
        </Wizard>
      </Activity>
    </Scenario>
  );
};

CreationWizardWithActivity.propTypes = {
  scenarioName: PropTypes.string.isRequired,
  activityName: PropTypes.string.isRequired,
  stepConfigs: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    view: PropTypes.func.isRequired,
  })).isRequired,
  getStepListConfig: PropTypes.func.isRequired,
  perfMarker: PropTypes.shape({
    createChild: PropTypes.func.isRequired,
  }).isRequired,
  scope: PropTypes.shape({
    save: PropTypes.func.isRequired,
  }).isRequired,
  siteMap: PropTypes.shape({}).isRequired,
  perfMarkerName: PropTypes.string.isRequired,
  onDoneNavigateToSiteMapId: PropTypes.string.isRequired,
  initialFlowFactors: PropTypes.shape({}),
  getFlow: PropTypes.func,
  isLegalFooter: PropTypes.bool,
  dynamicOptions: PropTypes.shape({}),
};

CreationWizardWithActivity.defaultProps = {
  initialFlowFactors: {},
  dynamicOptions: {},
  getFlow: undefined,
  isLegalFooter: false,
};

export const CreationWizard = withSiteMap(CreationWizardWithActivity);
